import { SegmentedControl } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";

import type { AiActorGender } from "../../../api/generated/model";
import { GlobalActors, NO_ACTORS_QUERY, type ActorsQuery } from "../../../platform/actors/library/GlobalActors";
import { MyActors } from "../../../platform/actors/library/MyActors";
import { useCreateTalkingHead } from "../../../platform/actors/talking-head-handoff";
import create from "../create/create.module.css";
import library from "../library/library.module.css";
import classes from "./simple-sections.module.css";

/** Pro's Actors page's columns on a phone, whatever the saved setting. */
const PHONE_COLUMNS = 2;

type Who = "global" | "team";

/**
 * Brand's Actors (spec ui-perf/simple-brand-tabs): the prototype's Global and the team's, Pro's
 * Actors page's own lists, with their search and facets kept across the two; an actor's Talking
 * head opens Simple's UGC video ad with it, and Make an actor opens Images' actor page.
 */
export function SimpleActors({ teamId, brandId }: { teamId: string; brandId: string }) {
  const [who, setWho] = useState<Who>("global");
  const talkingHead = useCreateTalkingHead("simple");
  const phone = useMediaQuery("(max-width: 48em)") === true;
  const columnsAtMost = phone ? PHONE_COLUMNS : undefined;
  const [query, setQuery] = useState<ActorsQuery>(NO_ACTORS_QUERY);
  const [gender, setGender] = useState<AiActorGender | undefined>(undefined);
  const [creators, setCreators] = useState<string[]>([]);
  const [product, setProduct] = useState<string | undefined>(undefined);
  const onQuery = (change: Partial<ActorsQuery>) => setQuery((current) => ({ ...current, ...change }));
  return (
    <section aria-label="Actors">
      <div className={classes.tabHead}>
        <SegmentedControl
          aria-label="Whose actors"
          radius="xl"
          size="xs"
          classNames={{ root: create.seg, label: create.segLabel }}
          value={who}
          onChange={(value) => setWho(value === "team" ? "team" : "global")}
          data={[
            { value: "global", label: "Global" },
            { value: "team", label: "Your team's" },
          ]}
        />
        <Link to="/teams/$teamId/brands/$brandId/simple/actor" params={{ teamId, brandId }} className={library.primary}>
          <Plus size={14} aria-hidden /> Make an actor
        </Link>
      </div>
      {who === "team" ? (
        <MyActors
          teamId={teamId}
          brandId={brandId}
          query={query}
          onQuery={onQuery}
          gender={gender}
          onGender={setGender}
          creators={creators}
          onCreators={setCreators}
          product={product}
          onProduct={setProduct}
          onTalkingHead={talkingHead}
          columnsAtMost={columnsAtMost}
        />
      ) : (
        <GlobalActors
          teamId={teamId}
          brandId={brandId}
          query={query}
          onQuery={onQuery}
          onTalkingHead={talkingHead}
          columnsAtMost={columnsAtMost}
        />
      )}
    </section>
  );
}
